
import { useState, useEffect, useRef } from 'react';
import { NoRecords, PageLoader } from '../../../lib/loader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';
import { TopupTransaction, useExportCardList } from '../index';
import { Search, Check, Copy, ExternalLink, Download } from 'lucide-react';
import { useDebounce } from '../../../hooks/debounce';
import { useParams } from 'react-router-dom';
import { copyToClipboard } from '../../../lib/copytoClipboard';
import { useModalContext } from '../../../contexts/ModalContext';
import { useToast } from '../../../hooks/usetoast';
import { useCheckAdminAccess } from '../../WhiteIp/api/whiteipapi';
import { logout } from '../../../hooks/logout';
import PermissionDenied from '../../common/permissionDenied';
import { WaitingLoader } from '../../../hooks/buttonLoad';
import { useBreadcrumbPagination } from '../../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../../utils/BreadcrumbPagination';

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { displayValue } from '../../../hooks/export';

function CardDepositHistory() {
    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const { id } = useParams();
    const [search, setSearch] = useState("");
    const { theme } = useModalContext();
    const toast = useToast();
    const searchText = useDebounce(search);
    const [double, setDouble] = useState(false);

    const { data: getAllCards } = useExportCardList({ type: "CardDeposit" })

    const { data: getTransactionHistory, isLoading } = TopupTransaction({
        id,
        page: searchText ? 0 : tempPage,
        size: tempRows,
        searchText,
        sortField: "createdAt",
        sortOrder: "desc",
        type: "CardDeposit"
    });

    // BreadCrumb Pagination

    const totalRecords = getTransactionHistory?.data?.totalRecords || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    });

    useEffect(() => {
        setTempPage(0);
        if (handleChangePage) {
            handleChangePage(null, 0);
        }
    }, [searchText]);

    const [fromEmail, setFromEmail] = useState(false);

    const copytxnhashAddress = async data => {
        setDouble(data._id)
        await copyToClipboard(data.txnHash)
        toast.success("Copied")
        setTimeout(() => {
            setDouble()
        }, 800)
    }
    const copyUserEmailAddress = async data => {
        setFromEmail(data._id)
        await copyToClipboard(data?.senderEmail)
        toast.success("Copied")
        setTimeout(() => {
            setFromEmail()
        }, 800)
    }

    const copyOrderId = async data => {

        await copyToClipboard(data?.order_id)
        toast.success("Copied")

    }

    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'History' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };


    const exportAllCards = async () => {
        try {
            const response = await getAllCards?.data;

            const allCards = response?.transactionHistory || [];
            exportToExcel(allCards, 'UserCardList.xlsx');
        } catch (error) {
            console.error('Export failed:', error);
        }
    };



    const exportToExcel = async (data, filename) => {
        if (!data || data.length === 0) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Cards');

        // Define columns to match your mapped fields
        worksheet.columns = [
            { header: 'Email', key: 'Email', width: 30 },
            { header: 'Txn_Hash', key: 'Txn_Hash', width: 45 },
            { header: 'Amount', key: 'Amount', width: 15 },
            { header: 'Currency', key: 'Currency', width: 12 },
            { header: 'CreatedAt', key: 'CreatedAt', width: 25 },
        ];

        // Style header row (optional but professional)
        worksheet.getRow(1).eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3F51B5' } }; // Indigo header
            cell.alignment = { horizontal: 'center' };
        });

        // Add rows with your exact mapped structure
        data.forEach(item => {
            worksheet.addRow({
                Email: item.senderEmail,
                Txn_Hash: item.txnHash,
                Amount: item.sentAmount,
                Currency: item.currencySymbol,
                CreatedAt: new Date(item.createdAt).toLocaleString(),
            });
        });

        // Auto-fit column widths for better UX
        worksheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                const length = cell.value ? String(cell.value).length : 10;
                if (length > maxLength) maxLength = length;
            });
            column.width = Math.min(maxLength + 2, 60); // Cap at 60 chars
        });

        // Generate & trigger download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, filename);
    };



    const renderInternalTransactionHistoryTable = () => (
        getTransactionHistory?.data?.transactionHistory?.length > 0 ? (
            <>
                <div style={{ minHeight: "60vh" }}>
                    <div className='table-responsive'>
                        <Table sx={{ minWidth: 650 }} aria-label="internal transaction history table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className='text-nowrap' align="center">S.NO</TableCell>
                                    <TableCell className='text-nowrap' align="center">Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">Order Id</TableCell>

                                    <TableCell className='text-nowrap' align="center">Txn Hash</TableCell>
                                    <TableCell className='text-nowrap' align="center">Amount</TableCell>
                                    <TableCell className='text-nowrap' align="center">Currency</TableCell>
                                    <TableCell className='text-nowrap' align="center">Status</TableCell>

                                    <TableCell className='text-nowrap' align="center">
                                        Date & Time{' '}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getTransactionHistory?.data?.transactionHistory?.map((row, i) => (
                                    <TableRow key={row?._id || i}>
                                        <TableCell className='text-nowrap' align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                                        <TableCell align="center">
                                            {row?.senderEmail || "-"}
                                            {fromEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyUserEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row?.order_id || "-"}
                                            <Copy
                                                onClick={() => copyOrderId(row)}
                                                size={20}
                                                className="ml-5"
                                                style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : "black" }}
                                            />

                                        </TableCell>

                                        <TableCell align="center">
                                            {row.txnHash ? (
                                                <>
                                                    {`${row.txnHash.slice(0, 3)}...${row.txnHash.slice(-3)}`}
                                                    {double === row._id ? (
                                                        <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : "black" }} />
                                                    ) : (
                                                        <Copy
                                                            onClick={() => copytxnhashAddress(row)}
                                                            size={20}
                                                            className="ml-5"
                                                            style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : "black" }}
                                                        />
                                                    )}
                                                    <a
                                                        href={row.explorerUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="ml-2"
                                                    >
                                                        <ExternalLink style={{ marginLeft: "5px" }} size={20} />
                                                    </a>
                                                </>
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>

                                        <TableCell className="text-nowrap" align="center">
                                            {row?.sentAmount ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    {displayValue(row.sentAmount, 6)}
                                                    {row?.currencySymbol ? (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            {row?.currencyImage && (
                                                                <img
                                                                    src={row.currencyImage}
                                                                    alt="Currency Icon"
                                                                    style={{
                                                                        height: '20px',
                                                                        width: '20px',
                                                                        borderRadius: '50%',
                                                                        verticalAlign: 'middle',
                                                                        objectFit: 'cover',
                                                                    }}
                                                                />
                                                            )}
                                                        </span>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell className="text-nowrap" align="center">{row.currencySymbol}</TableCell>
                                        <TableCell className="text-nowrap" align="center">
                                            <span style={{ color: row?.status ? "green" : "red", fontWeight: 600 }}>
                                                {row?.status ? "Completed" : "Failed"}
                                            </span>
                                        </TableCell>

                                        <TableCell className='text-nowrap' align="center">
                                            {moment(row?.createdAt).format("DD-MM-YYYY | hh:mm A")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </>
        ) : (
            <NoRecords />
        )
    );

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        const tableContent = isLoading ? (
            <PageLoader />
        ) : (
            renderInternalTransactionHistoryTable()
        );

        content = (
            <section>
                <h4>Card Created History</h4>
                <div className="ad_pg_bx bg-white p-3">
                    <div
                        className="d-flex justify-content-between align-items-center mb-2"
                        style={{ flexWrap: 'wrap' }}
                    >
                        <div className="d-flex align-items-center" style={{ gap: '5px' }}>
                            <div className="input-group" style={{ width: '400px' }}>
                                <span className="input-group-icon">
                                    <Search size={20} color="#aaa9a9" />
                                </span>
                                <input
                                    type="text"
                                    ref={inputRef}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="form-control"
                                    placeholder="Search by name , email"
                                    value={search}
                                    style={{ width: '200px' }}
                                />
                            </div>
                        </div>
                        <button
                            className="btn btn-success"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap',
                            }}
                            onClick={() => exportAllCards()}
                        >
                            <Download size={15} style={{ marginRight: '5px' }} />
                            Export
                        </button>
                    </div>
                    {tableContent}
                </div>
            </section>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;

}

export default CardDepositHistory;